import { useEffect, useState, type ReactNode } from 'react';
import { Wand2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RelativeTime } from '@/components/RelativeTime';
import { agenciesService, type AgencySetupWizard } from '@/services/agencies';
import { setupWizardStepLabel as stepLabel } from '@/lib/setup-wizard';
import { EmptyBlock } from '@/pages/templates/components/EmptyBlock';

const STATUS: Record<AgencySetupWizard['status'], { label: string; tone: BadgeTone }> = {
  not_started: { label: 'Not started', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'info' },
  left_early: { label: 'Left early', tone: 'warning' },
  finished: { label: 'Finished', tone: 'success' },
};

/** The agency page's "Setup wizard" card: whether the agency uses the dashboard's setup wizard and where it left. */
export function SetupWizardCard({ agencyPublicId }: { agencyPublicId: string }) {
  const [summary, setSummary] = useState<AgencySetupWizard | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setFailed(false);
    agenciesService.getAgencySetupWizard(agencyPublicId).then(
      (data) => { if (!cancelled) setSummary(data); },
      () => { if (!cancelled) setFailed(true); },
    );
    return () => { cancelled = true; };
  }, [agencyPublicId]);

  return (
    <Card className="py-0 gap-0 shadow-sm">
      <CardHeader className="px-5 py-4 gap-0 border-b">
        <CardTitle className="text-base">Setup wizard</CardTitle>
        <CardDescription>Whether they started it, where they left, and if they finished</CardDescription>
      </CardHeader>
      <CardContent className="p-5">
        {failed ? (
          <p className="text-sm text-muted-foreground">Couldn't load the setup wizard.</p>
        ) : summary ? (
          <SetupWizardCardBody summary={summary} />
        ) : (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SetupWizardCardBody({ summary }: { summary: AgencySetupWizard }) {
  const status = STATUS[summary.status];
  const badge = <Badge tone={status.tone}>{status.label}</Badge>;

  if (summary.status === 'not_started') {
    return (
      <div className="py-8 text-center">
        <EmptyBlock icon={Wand2} title="Not started" description="Hasn't opened the setup wizard yet." />
      </div>
    );
  }

  // Relative, with the full date in the admin's timezone on hover, as the page's other dates.
  const when = (iso: string | null) => (iso ? <RelativeTime date={iso} /> : null);

  const rows: { label: string; value: ReactNode }[] = [
    { label: 'Status', value: badge },
    { label: 'Started', value: when(summary.startedAt) ?? 'Not recorded' },
    { label: 'Furthest step', value: summary.furthestStep ? stepLabel(summary.furthestStep) : 'Not recorded' },
    { label: 'Left at step', value: summary.leftEarlyAtStep ? stepLabel(summary.leftEarlyAtStep) : 'No' },
    { label: 'Test lead sent', value: summary.testLeadSent ? 'Yes' : 'No' },
    { label: 'Published', value: when(summary.publishedAt) ?? 'Not yet' },
    { label: 'Last activity', value: when(summary.lastActivityAt) ?? 'Not recorded' },
  ];

  return (
    <div className="space-y-4 text-sm">
      {rows.map((row) => (
        <div key={row.label} className="flex justify-between items-center gap-4">
          <span className="text-muted-foreground shrink-0">{row.label}</span>
          <span className="font-medium text-right">{row.value}</span>
        </div>
      ))}
    </div>
  );
}
